import React from "react";
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Stack,
  Chip,
  Box,
} from "@mui/material";
import {
  NodeSchedule,
  RichSession,
} from "../../../redux/reducers/scheduleReducer";
import SessionChip from "./SessionChip";

interface Props {
  node: NodeSchedule;
  selected: boolean;
  onSelect: () => void;
  onSelectSession: (s: RichSession) => void;
  selectedSession?: RichSession | null;
  drawerOpen?: boolean;
}

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "running":
    case "active":
      return "#27ae60"; // Green
    case "idle":
      return "#f39c12"; // Orange
    case "stopped":
      return "#7f8c8d"; // Gray
    case "error":
      return "#c0392b"; // Red
    case "initializing":
      return "#2980b9"; // Blue
    default:
      return "#95a5a6"; // Default gray
  }
};

const NodeCard: React.FC<Props> = ({
  node,
  selected,
  onSelect,
  onSelectSession,
  selectedSession,
  drawerOpen = false,
}) => {
  // Check if any session in this node is selected
  const hasSelectedSession = selectedSession && 
    node.sessions.some(s => 
      s.s?.session_id === selectedSession.s?.session_id &&
      s.is_phantom === selectedSession.is_phantom
    );

  // Determine border style based on selection state
  const getBorderStyle = () => {
    if (hasSelectedSession) {
      // Muted border when a session in this node is selected
      return "2px solid #cbd5e0";
    } else if (selected) {
      // Full blue border when node itself is selected
      return "2px solid #1976d2";
    } else {
      // Transparent border when nothing is selected
      return "2px solid transparent";
    }
  };

  const getElevation = () => {
    if (hasSelectedSession || selected) {
      return 2;
    }
    return 1;
  };

  return (
    <Card
      elevation={getElevation()}
      sx={{
        border: getBorderStyle(),
        transition: "all 0.2s",
        height: "100%",
        minHeight: 150,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        opacity: hasSelectedSession ? 0.95 : 1,
      }}
    >
      {/* Status chip in top right */}
      <Chip
        label={node.node_status}
        size="small"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          height: 22,
          fontSize: "0.7rem",
          backgroundColor: getStatusColor(node.node_status),
          color: "#fff",
          zIndex: 1,
        }}
      />
      
      <CardActionArea 
        onClick={onSelect} 
        sx={{ 
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
        }}
      >
        <CardContent sx={{ width: "100%", height: "100%", pt: 4 }}>
          <Box sx={{ mb: 1, minHeight: "48px" }}>
            <Typography 
              variant="body2" 
              fontWeight={600} 
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                fontSize: "0.875rem",
                lineHeight: 1.3,
                pr: 8, // Space for status chip
              }}
              title={node.node_id}
            >
              {node.node_id}
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                mt: 0.5, 
                display: "block",
                height: "16px", // Reserve space even when empty
                lineHeight: "16px",
              }}
            >
              {node.node_idle_s != null ? `idle: ${Math.floor(node.node_idle_s)}s` : '\u00A0'}
            </Typography>
          </Box>
          <Stack direction="column" spacing={0.5} sx={{ overflow: "hidden" }}>
            {node.sessions.slice(0, 3).map((s, idx) => {
              const isSelected = Boolean(selectedSession && 
                selectedSession.s?.session_id === s.s?.session_id &&
                selectedSession.is_phantom === s.is_phantom);
              
              return (
                <SessionChip
                  key={idx}
                  session={s}
                  onClick={() => onSelectSession(s)}
                  selected={isSelected}
                  drawerOpen={drawerOpen}
                />
              );
            })}
            {node.sessions.length > 3 && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                +{node.sessions.length - 3} more sessions
              </Typography>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default NodeCard;
